import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Archive, Download, RefreshCw, Power, AlertTriangle, Check, Lock, Copy, ChevronDown, ChevronRight, Save, Calendar, Users, X as XIcon, EyeOff, Eye } from "lucide-react";
import api, { getToken, getLevel } from "@/lib/api";
import { toast } from "sonner";

const STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  draft: "bg-amber-100 text-amber-800 border-amber-200",
  archived: "bg-slate-200 text-slate-700 border-slate-300",
};

function StatusPill({ status }) {
  const cls = STATUS_BADGE[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-[0.14em] ${cls}`}>
      {status}
    </span>
  );
}

function CreateSeasonModal({ onClose, onCreated, existingIds, activeId }) {
  const currentYear = new Date().getFullYear();
  const [seasonId, setSeasonId] = useState(String(currentYear + 1));
  const [displayName, setDisplayName] = useState("");
  const [cloneFrom, setCloneFrom] = useState(activeId || "");
  const [busy, setBusy] = useState(false);

  const yearOptions = useMemo(() => {
    const opts = [];
    for (let y = currentYear; y <= currentYear + 6; y++) {
      opts.push(String(y));
    }
    return opts;
  }, [currentYear]);

  const taken = existingIds.includes(seasonId);

  const submit = async () => {
    if (taken) {
      toast.error(`Season ${seasonId} already exists.`);
      return;
    }
    setBusy(true);
    try {
      const body = { season_id: seasonId };
      if (displayName.trim()) body.display_name = displayName.trim();
      if (cloneFrom) body.clone_from = cloneFrom;
      const r = await api.post("/admin/season-config", body);
      toast.success(`Created ${r.data.season.display_name} (draft).`);
      onCreated(r.data.season);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create season.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="create-season-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-slate-900">Open a New Season</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="create-season-close">✕</button>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Pick a year and (optionally) clone from an existing season. The new season starts as a <strong>draft</strong> — you can edit dates before activating.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Year</label>
            <select
              value={seasonId}
              onChange={(e) => setSeasonId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono"
              data-testid="create-season-year"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y} disabled={existingIds.includes(y)}>
                  {y}{existingIds.includes(y) ? " (exists)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Display Name</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={`Summer ${seasonId}`}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              data-testid="create-season-displayname"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Clone From</label>
            <select
              value={cloneFrom}
              onChange={(e) => setCloneFrom(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              data-testid="create-season-clonefrom"
            >
              <option value="">(empty — start blank)</option>
              {existingIds.map((id) => (
                <option key={id} value={id}>{id}{id === activeId ? " (active)" : ""}</option>
              ))}
            </select>
            <div className="text-[11px] text-slate-500 mt-1">Day dates and meetings will auto-shift by year delta.</div>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="create-season-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || taken}
            className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="create-season-submit"
          >
            <Plus className="w-4 h-4" /> {busy ? "Creating…" : "Create Season"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel = "Confirm", confirmClass = "bg-blue-600 hover:bg-blue-700", onConfirm, onCancel }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" data-testid="confirm-modal">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{message}</p>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="confirm-cancel">Cancel</button>
          <button
            onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}
            disabled={busy}
            className={`px-3 py-1.5 text-sm rounded-lg text-white font-semibold disabled:opacity-50 ${confirmClass}`}
            data-testid="confirm-submit"
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function CampDaysModal({ days, readonly, onClose, onSave, campStart, campEnd }) {
  const [local, setLocal] = useState(() => {
    // Normalize to 38 slots (or whatever was provided)
    const max = Math.max(38, ...(days || []).map((d) => d.day_num || 0));
    const map = new Map((days || []).map((d) => [d.day_num, d]));
    const out = [];
    for (let i = 1; i <= max; i++) {
      const existing = map.get(i);
      out.push(existing ? { ...existing } : { day_num: i, date: "", skipped: false });
    }
    return out;
  });

  const setDay = (i, patch) => setLocal((cur) => {
    const next = [...cur];
    next[i] = { ...next[i], ...patch };
    return next;
  });

  const handleSave = () => {
    // Strip nothing — pass everything through; backend validates
    onSave(local);
  };

  const activeCount = local.filter((d) => !d.skipped && d.date).length;
  const skippedCount = local.filter((d) => d.skipped).length;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="camp-days-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-2">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" /> Camp Day Calendar
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Pick a date for each of the {local.length} camp days. Toggle <strong>Skip</strong> on any day that doesn't count (e.g. an off-day).
              {campStart && campEnd && <> Camp window: <span className="font-mono">{campStart} → {campEnd}</span>.</>}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid="camp-days-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center gap-3 flex-wrap text-xs">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-bold uppercase tracking-[0.14em]">
            <Eye className="w-3 h-3" /> {activeCount} counted
          </span>
          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-200 text-slate-700 font-bold uppercase tracking-[0.14em]">
            <EyeOff className="w-3 h-3" /> {skippedCount} skipped
          </span>
          {!readonly && (
            <button
              onClick={() => {
                // Bulk: clear all dates AND skipped flags
                if (window.confirm("Clear all dates? (Skip flags are also reset.)")) {
                  setLocal((cur) => cur.map((d) => ({ ...d, date: "", skipped: false })));
                }
              }}
              className="ml-auto text-xs text-slate-500 hover:text-rose-600 underline"
              data-testid="camp-days-clear"
            >
              Clear all
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {local.map((d, i) => (
              <div
                key={d.day_num}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                  d.skipped ? "bg-slate-100/50 border-slate-200 opacity-60" : "bg-white border-slate-200"
                }`}
                data-testid={`day-row-${d.day_num}`}
              >
                <div className="w-14 shrink-0 font-mono text-sm font-bold text-slate-700">Day {d.day_num}</div>
                <input
                  type="date"
                  value={d.date || ""}
                  disabled={readonly || d.skipped}
                  onChange={(e) => setDay(i, { date: e.target.value })}
                  className="flex-1 min-w-0 px-2 py-1 rounded border border-slate-200 text-sm font-mono disabled:bg-slate-100 disabled:text-slate-400"
                  data-testid={`day-date-input-${d.day_num}`}
                />
                <label className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    disabled={readonly}
                    checked={!!d.skipped}
                    onChange={(e) => setDay(i, { skipped: e.target.checked, date: e.target.checked ? "" : d.date })}
                    data-testid={`day-skip-${d.day_num}`}
                  />
                  Skip
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm">Close</button>
          {!readonly && (
            <button
              onClick={handleSave}
              className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm inline-flex items-center gap-1.5"
              data-testid="camp-days-save"
            >
              <Save className="w-4 h-4" /> Apply Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Canonical 4-meeting structure for 2027+. Rules are FIXED — admin only picks dates.
const MEETING_BLUEPRINTS = [
  {
    mtg_num: 1, label: "Meeting 1",
    description: "First-Year Staff Orientation",
    rule: "Applies to staff with 1 year or less at camp.",
    kind: "single",  // single date
    applies_to_divisions: "FIRST_YEAR_ONLY",
  },
  {
    mtg_num: 2, label: "Meeting 2",
    description: "Returning Staff Orientation",
    rule: "Applies to staff with 2+ years at camp.",
    kind: "single",
    applies_to_divisions: "RETURNING_ONLY",
  },
  {
    mtg_num: 3, label: "Meeting 3",
    description: "Two-night orientation (Non-Group + Group)",
    rule: "Two dates — one for Non-Group divisions (everyone except Mariners / Skippers / Navigators) and one for Group divisions (Mariners / Skippers / Navigators).",
    kind: "split",
  },
  {
    mtg_num: 4, label: "Meeting 4",
    description: "Meet Your Mateys",
    rule: "Applies to Group divisions only (Mariners / Skippers / Navigators).",
    kind: "single",
    applies_to_divisions: "GROUP_ONLY",
  },
];

function MeetingsModal({ meetings, readonly, onClose, onSave }) {
  const [local, setLocal] = useState(() => {
    const byNum = new Map((meetings || []).map((m) => [m.mtg_num, m]));
    return MEETING_BLUEPRINTS.map((bp) => {
      const existing = byNum.get(bp.mtg_num) || {};
      if (bp.kind === "single") {
        return { mtg_num: bp.mtg_num, date: existing.date || "",
                 applies_to_divisions: bp.applies_to_divisions };
      }
      // split
      return { mtg_num: bp.mtg_num,
               non_group_date: existing.non_group_date || existing.date || "",
               group_date: existing.group_date || "" };
    });
  });

  const setEntry = (idx, patch) => setLocal((cur) => {
    const next = [...cur];
    next[idx] = { ...next[idx], ...patch };
    return next;
  });

  const swap = (idx) => setLocal((cur) => {
    const next = [...cur];
    next[idx] = { ...next[idx],
                  group_date: cur[idx].non_group_date,
                  non_group_date: cur[idx].group_date };
    return next;
  });

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="meetings-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-2">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" /> Orientation Meetings
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              The rules below are fixed — just pick a date for each meeting. Leave blank to skip a meeting entirely.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid="meetings-close">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {MEETING_BLUEPRINTS.map((bp, idx) => {
            const entry = local[idx];
            return (
              <div key={bp.mtg_num} className="rounded-xl border border-slate-200 p-4 bg-slate-50/40" data-testid={`mtg-card-${bp.mtg_num}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold uppercase tracking-[0.14em]">{bp.label}</span>
                  <span className="font-semibold text-slate-900 text-sm">{bp.description}</span>
                </div>
                <div className="text-xs text-slate-600 mt-1">{bp.rule}</div>

                {bp.kind === "single" ? (
                  <div className="mt-3 max-w-xs">
                    <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Date</label>
                    <input
                      type="date"
                      disabled={readonly}
                      value={entry.date || ""}
                      onChange={(e) => setEntry(idx, { date: e.target.value })}
                      className="block w-full mt-1 px-2 py-1.5 rounded border border-slate-200 text-sm font-mono disabled:bg-slate-100"
                      data-testid={`mtg-date-${bp.mtg_num}`}
                    />
                  </div>
                ) : (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Non-Group Date</label>
                      <div className="text-[10px] text-slate-500 mb-1">(everyone except Mariners / Skippers / Navigators)</div>
                      <input
                        type="date"
                        disabled={readonly}
                        value={entry.non_group_date || ""}
                        onChange={(e) => setEntry(idx, { non_group_date: e.target.value })}
                        className="block w-full px-2 py-1.5 rounded border border-slate-200 text-sm font-mono disabled:bg-slate-100"
                        data-testid={`mtg-${bp.mtg_num}-nongroup`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Group Date</label>
                      <div className="text-[10px] text-slate-500 mb-1">(Mariners / Skippers / Navigators)</div>
                      <input
                        type="date"
                        disabled={readonly}
                        value={entry.group_date || ""}
                        onChange={(e) => setEntry(idx, { group_date: e.target.value })}
                        className="block w-full px-2 py-1.5 rounded border border-slate-200 text-sm font-mono disabled:bg-slate-100"
                        data-testid={`mtg-${bp.mtg_num}-group`}
                      />
                    </div>
                    {!readonly && (
                      <button
                        onClick={() => swap(idx)}
                        className="sm:col-span-2 text-xs text-blue-700 hover:underline self-start"
                        data-testid={`mtg-${bp.mtg_num}-swap`}
                      >
                        ⇄ Swap Group / Non-Group dates
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm">Close</button>
          {!readonly && (
            <button
              onClick={() => onSave(local)}
              className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm inline-flex items-center gap-1.5"
              data-testid="meetings-save"
            >
              <Save className="w-4 h-4" /> Apply Changes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SeasonStatsPanel({ seasonId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/season-config/${seasonId}/stats`)
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [seasonId]);
  if (loading) return <div className="text-sm text-slate-400 py-3">Loading stats…</div>;
  if (!stats) return null;
  const t = stats.totals || {};
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/40 p-4" data-testid="season-stats-panel">
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Season Stats — read-only snapshot</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Crew Members" value={t.crew_members} />
        <Stat label="Staff with Sign-ins" value={t.distinct_staff_with_signins} />
        <Stat label="Total Sign-ins" value={t.total_signins} />
        <Stat label="Early Departures" value={t.early_departures} />
        <Stat label="Planned Absences" value={t.planned_absences} />
        <Stat label="Visitor Sign-ins" value={t.visitor_signins} />
        <Stat label="Waiver Signatures" value={t.waiver_signatures} />
      </div>
      {Object.keys(stats.signins_by_status || {}).length > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
          <span className="text-slate-500 font-bold uppercase tracking-[0.14em] text-[10px]">By Status:</span>
          {Object.entries(stats.signins_by_status).map(([s, n]) => (
            <span key={s} className="px-2 py-0.5 rounded-full bg-white border border-slate-200 font-mono">{s}: <strong>{n}</strong></span>
          ))}
        </div>
      )}
      {(stats.top_5_planned_absences || []).length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1.5">Top 5 by planned absences</div>
          <ol className="text-xs text-slate-700 space-y-0.5 list-decimal list-inside">
            {stats.top_5_planned_absences.map((r) => (
              <li key={r.crew_id}><span className="font-semibold">{r.name}</span> <span className="text-slate-500">— {r.count} days</span></li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-2.5">
      <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
      <div className="font-display text-lg font-bold text-slate-900 tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

function SeasonDetail({ season, onReload }) {
  const [edited, setEdited] = useState(null);
  const [showDayDates, setShowDayDates] = useState(false);
  const [showMeetings, setShowMeetings] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => { setEdited(null); }, [season?._id]);

  const readonly = season.status === "archived" || season.is_hardcoded_2026;
  const draft = season.status === "draft";
  const active = season.status === "active";
  const archived = season.status === "archived";

  const view = edited || season;

  const field = (key, val) => {
    if (edited) {
      setEdited({ ...edited, [key]: val });
    } else {
      setEdited({ ...season, [key]: val });
    }
  };

  const saveEdits = async () => {
    if (!edited) return;
    try {
      const r = await api.put(`/admin/season-config/${season._id}`, {
        display_name: edited.display_name,
        camp_start_date: edited.camp_start_date,
        camp_end_date: edited.camp_end_date,
        payroll_split_date: edited.payroll_split_date,
        campminder_season_id: typeof edited.campminder_season_id === "string"
          ? parseInt(edited.campminder_season_id, 10) : edited.campminder_season_id,
        operating_hours: edited.operating_hours,
        day_dates: edited.day_dates,
        meeting_dates: edited.meeting_dates,
        planned_absence_dates: edited.planned_absence_dates,
      });
      toast.success("Saved.");
      setEdited(null);
      onReload();
    } catch (e) {
      const errs = e?.response?.data?.detail?.errors;
      if (Array.isArray(errs)) toast.error(`Validation: ${errs.join("; ")}`);
      else toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const activate = async () => {
    try {
      await api.post(`/admin/season-config/${season._id}/activate`);
      toast.success(`${season.display_name} is now active.`);
      onReload();
    } catch (e) {
      const errs = e?.response?.data?.detail?.errors;
      toast.error(Array.isArray(errs) ? errs.join("; ") : (e?.response?.data?.detail || "Activate failed."));
    } finally { setConfirm(null); }
  };

  const archiveSeason = async () => {
    try {
      const r = await api.post(`/admin/season-config/${season._id}/archive`);
      toast.success(`Archived. ZIP ready to download.`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed.");
    } finally { setConfirm(null); }
  };

  const downloadZip = async () => {
    setDownloading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/season-config/${season._id}/archive.zip`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `rrdc-archive-${season._id}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally { setDownloading(false); }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      await api.post(`/admin/season-config/${season._id}/regenerate-archive`);
      toast.success("Archive regenerated.");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed.");
    } finally { setRegenerating(false); }
  };

  const downloadReport = async (kind) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/season-config/${season._id}/${kind}.xlsx`;
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `rrdc-${kind}-${season._id}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(`${kind} download failed: ${e.message}`);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid={`season-detail-${season._id}`}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display font-bold text-xl text-slate-900" data-testid="season-display-name">{view.display_name}</h2>
            <StatusPill status={season.status} />
            {season.is_hardcoded_2026 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-bold uppercase tracking-[0.14em]">
                <Lock className="w-3 h-3" /> Hardcoded
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">Season ID: <span className="font-mono">{season._id}</span></div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {edited && !readonly && (
            <button onClick={saveEdits} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1" data-testid="season-save">
              <Save className="w-4 h-4" /> Save Changes
            </button>
          )}
          {draft && !edited && (
            <button
              onClick={() => setConfirm({
                title: `Activate ${season.display_name}?`,
                message: `This will become the active season. The current active season will be archived. All sync jobs (CampMinder, etc.) will use ${season._id} dates after this.`,
                confirmLabel: "Activate Season",
                confirmClass: "bg-emerald-600 hover:bg-emerald-700",
                onConfirm: activate,
              })}
              className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1"
              data-testid="season-activate"
            >
              <Power className="w-4 h-4" /> Activate Season
            </button>
          )}
          {active && (
            <button
              onClick={() => setConfirm({
                title: `Archive ${season.display_name}?`,
                message: `This will make the season read-only and generate a downloadable archive ZIP with all data (sign-ins, planned absences, visitors, waivers, etc.).\n\nNo data is deleted. You'll be able to download or regenerate the archive any time after.`,
                confirmLabel: "Archive Season",
                confirmClass: "bg-amber-600 hover:bg-amber-700",
                onConfirm: archiveSeason,
              })}
              className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold inline-flex items-center gap-1"
              data-testid="season-archive"
            >
              <Archive className="w-4 h-4" /> Archive Season
            </button>
          )}
          {archived && season.archive_zip_gridfs_id && (
            <>
              <button onClick={downloadZip} disabled={downloading} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="season-download">
                <Download className="w-4 h-4" /> {downloading ? "Downloading…" : "Download Archive"}
              </button>
              <button onClick={regenerate} disabled={regenerating} className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm inline-flex items-center gap-1 disabled:opacity-50" data-testid="season-regenerate">
                <RefreshCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} /> Regenerate
              </button>
            </>
          )}
        </div>
      </div>

      {season.is_hardcoded_2026 && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
          <strong>2026 calendar is hardcoded in code</strong> to lock in the live camp. To change dates for next year, click <em>Create New Season</em> above (it clones 2026 as a starting point).
        </div>
      )}
      {archived && (
        <div className="mb-4 p-3 rounded-lg bg-slate-100 border border-slate-300 text-sm text-slate-700 inline-flex items-center gap-2">
          <Lock className="w-4 h-4" /> Archived — read-only. Data is preserved and downloadable.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Box label="Display Name">
          <input disabled={readonly} value={view.display_name || ""} onChange={(e) => field("display_name", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50" data-testid="season-input-displayname" />
        </Box>
        <Box label="CampMinder Season ID">
          <input disabled={readonly} type="number" value={view.campminder_season_id ?? ""} onChange={(e) => field("campminder_season_id", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono disabled:bg-slate-50" data-testid="season-input-csid" />
        </Box>
        <Box label="Camp Start Date">
          <input disabled={readonly} type="date" value={view.camp_start_date || ""} onChange={(e) => field("camp_start_date", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono disabled:bg-slate-50" data-testid="season-input-startdate" />
        </Box>
        <Box label="Camp End Date">
          <input disabled={readonly} type="date" value={view.camp_end_date || ""} onChange={(e) => field("camp_end_date", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono disabled:bg-slate-50" data-testid="season-input-enddate" />
        </Box>
        <Box label="Payroll Split Date">
          <input disabled={readonly} type="date" value={view.payroll_split_date || ""} onChange={(e) => field("payroll_split_date", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono disabled:bg-slate-50" data-testid="season-input-splitdate" />
        </Box>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="schedule-buttons">
        <button
          type="button"
          onClick={() => setShowDayDates(true)}
          className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 border-blue-200 bg-blue-50/50 hover:bg-blue-50 hover:border-blue-300 text-left transition"
          data-testid="open-camp-days-modal"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Calendar className="w-5 h-5 text-blue-600 shrink-0" />
            <div className="min-w-0">
              <div className="font-display font-bold text-slate-900 text-sm">Edit Camp Days</div>
              <div className="text-xs text-slate-500">
                {(view.day_dates || []).filter((d) => !d.skipped && d.date).length} dated ·{" "}
                {(view.day_dates || []).filter((d) => d.skipped).length} skipped ·{" "}
                {(view.day_dates || []).length} total
              </div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
        </button>

        <button
          type="button"
          onClick={() => setShowMeetings(true)}
          className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 border-blue-200 bg-blue-50/50 hover:bg-blue-50 hover:border-blue-300 text-left transition"
          data-testid="open-meetings-modal"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Users className="w-5 h-5 text-blue-600 shrink-0" />
            <div className="min-w-0">
              <div className="font-display font-bold text-slate-900 text-sm">Edit Orientation Meetings</div>
              <div className="text-xs text-slate-500">
                4 meetings · Mtg 1 (1st-yr) · Mtg 2 (returning) · Mtg 3 (split) · Mtg 4 (Group)
              </div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      </div>

      {showDayDates && (
        <CampDaysModal
          days={view.day_dates || []}
          readonly={readonly}
          campStart={view.camp_start_date}
          campEnd={view.camp_end_date}
          onClose={() => setShowDayDates(false)}
          onSave={(updated) => {
            field("day_dates", updated);
            setShowDayDates(false);
            toast.success("Camp Days updated. Click Save Changes to commit.");
          }}
        />
      )}
      {showMeetings && (
        <MeetingsModal
          meetings={view.meeting_dates || []}
          readonly={readonly}
          onClose={() => setShowMeetings(false)}
          onSave={(updated) => {
            field("meeting_dates", updated);
            setShowMeetings(false);
            toast.success("Orientation Meetings updated. Click Save Changes to commit.");
          }}
        />
      )}

      <SeasonStatsPanel seasonId={season._id} />

      <div className="mt-5 pt-4 border-t border-slate-100">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Season Reports (XLSX)</div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => downloadReport("payroll")}
            className="px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-800 text-sm font-semibold inline-flex items-center gap-1.5"
            data-testid="download-payroll-xlsx"
          >
            <Download className="w-4 h-4" /> Payroll Export
          </button>
          <button
            onClick={() => downloadReport("attendance")}
            className="px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-semibold inline-flex items-center gap-1.5"
            data-testid="download-attendance-xlsx"
          >
            <Download className="w-4 h-4" /> Attendance Summary
          </button>
          <span className="text-xs text-slate-500 ml-2">Scoped to {view.camp_start_date} → {view.camp_end_date}</span>
        </div>
      </div>

      <div className="mt-5 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
        <span>Created {season.created_at_utc?.slice(0, 10)} by {season.created_by}</span>
        {season.activated_at_utc && <span>· Activated {season.activated_at_utc?.slice(0, 10)}</span>}
        {season.archived_at_utc && <span>· Archived {season.archived_at_utc?.slice(0, 10)} by {season.archived_by}</span>}
        {season.cloned_from && <span>· Cloned from {season.cloned_from}</span>}
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          confirmClass={confirm.confirmClass}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function Box({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Section({ title, open, setOpen, testId, children }) {
  return (
    <div className="mt-4 rounded-xl border border-slate-200 overflow-hidden" data-testid={testId}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50">
        <span className="font-display font-bold text-sm text-slate-900">{title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-slate-100">{children}</div>}
    </div>
  );
}

export default function SeasonConfigPage() {
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!getToken()) { navigate("/"); return; }
    if (getLevel() !== "leadership") {
      toast.error("Season Config is leadership-only.");
      navigate("/settings");
    }
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/season-config");
      const items = r.data.items || [];
      setSeasons(items);
      // Auto-select active or first
      const active = items.find((s) => s.status === "active");
      const next = active?._id || items[0]?._id || null;
      setSelectedId((cur) => cur && items.some((s) => s._id === cur) ? cur : next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load seasons.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedId) { setSelectedDoc(null); return; }
    api.get(`/admin/season-config/${selectedId}`)
      .then((r) => setSelectedDoc(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load season."));
  }, [selectedId]);

  const existingIds = seasons.map((s) => s._id);
  const activeSeason = seasons.find((s) => s.status === "active");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/settings")} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 font-semibold text-sm" data-testid="back-to-settings">
            <ArrowLeft className="w-4 h-4" /> Settings
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Rolling River</div>
            <h1 className="font-display text-lg sm:text-xl font-bold text-slate-900">Season Management</h1>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
            data-testid="open-new-season"
          >
            <Plus className="w-4 h-4" /> Create New Season
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Season list sidebar */}
        <aside className="rounded-2xl border border-slate-200 bg-white p-3 h-fit" data-testid="seasons-sidebar">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 px-2 pb-2">All Seasons</div>
          {loading ? (
            <div className="px-3 py-6 text-center text-slate-400 text-sm">Loading…</div>
          ) : seasons.length === 0 ? (
            <div className="px-3 py-6 text-center text-slate-400 text-sm">No seasons yet.</div>
          ) : seasons.map((s) => (
            <button
              key={s._id}
              onClick={() => setSelectedId(s._id)}
              className={`w-full text-left px-3 py-2 rounded-lg transition mb-1 border ${
                selectedId === s._id ? "bg-blue-50 border-blue-300" : "border-transparent hover:bg-slate-50"
              }`}
              data-testid={`sidebar-season-${s._id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-slate-900 text-sm">{s.display_name || s._id}</span>
                <StatusPill status={s.status} />
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                {s.camp_start_date} → {s.camp_end_date}
              </div>
            </button>
          ))}
        </aside>

        {/* Detail panel */}
        <div>
          {!selectedDoc ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-400">
              {loading ? "Loading…" : "Pick a season on the left."}
            </div>
          ) : (
            <SeasonDetail season={selectedDoc} onReload={() => { load(); api.get(`/admin/season-config/${selectedId}`).then((r) => setSelectedDoc(r.data)); }} />
          )}
        </div>
      </main>

      {showCreate && (
        <CreateSeasonModal
          onClose={() => setShowCreate(false)}
          onCreated={(s) => { setSelectedId(s._id); load(); }}
          existingIds={existingIds}
          activeId={activeSeason?._id}
        />
      )}
    </div>
  );
}
